import { isSuggestedTag } from "@/lib/tagScope";
import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Plus, Link, ImageIcon, X, AlarmClock, Pencil } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { motion, AnimatePresence } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import normalizeUrl from "normalize-url";

import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { UnifiedModal } from "@/components/ui/unified-modal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { toast } from "sonner";
import { apiClient } from "@/lib/api/client";
import { handleInputFocus } from "@/utils/keyboard";
import { TagPickerStacked } from "@/components/TagPickerStacked";
import { ReminderFormModal } from "@/components/modals/ReminderFormModal";
import { TAG_COLORS, tagsService, type Tag } from "@/services/tags.service";
import { notificationsService } from "@/services/notifications.service";
import { sortTags } from "@/lib/utils/tagSorting";
import { ClipboardUrlInputRow } from "@/components/shared/ClipboardUrlInputRow";
import { parseHttpUrlOrNull } from "@/utils/urlInput";
import { cn } from "@/lib/utils";
import { isNativeApp } from "@/utils/platform-detection";
import { TagPill } from "@/components/TagPill";
import { CollectionPickerStacked } from "@/components/CollectionPickerStacked";
import { useWritableCollections } from "@/hooks/useCollections";
import type { Collection } from "@/types/collections";
import { CARD_TITLE, LABEL, META, ROW_TEXT, ROW_TITLE, SECTION_TITLE } from "@/lib/typography";

// -- Types ------------------------------------------------------------------

type ContentType = "link" | "image" | "note";

interface AddContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveLink?: (url: string) => void;
  contentType?: ContentType;
  initialUrl?: string;
  initialNote?: string;
  initialTagName?: string;
  /** Pre-selected files from the FAB (skips the internal file picker). */
  initialFiles?: File[];
  /** If true, the user cannot edit URL/note/tags (guided flow). */
  disableInputs?: boolean;
  /** If true, hide the "Add Another" button in the success state. */
  disableAddAnother?: boolean;
}

type ModalState = "input" | "processing" | "success";

// -- Constants --------------------------------------------------------------

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500 MB
const MAX_IMAGE_COUNT = 10;

const ACCEPTED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
].join(",");

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const STASHT_HOSTS = new Set(["stasht.app", "app.stasht.app", "admin.stasht.app", "localhost", "127.0.0.1"]);

interface SaveResponse {
  stasht_id: string;
  status: string;
}

// -- URL helpers (unchanged) ------------------------------------------------

const isUuid = (value: string): boolean => UUID_REGEX.test(value.trim());

const extractStashtIdFromUrl = (url: string): string | null => {
  try {
    const normalized = normalizeUrl(url);
    const urlObj = new URL(normalized);
    const hostname = urlObj.hostname.toLowerCase().replace(/^www\./, "");
    if (!STASHT_HOSTS.has(hostname)) {
      return null;
    }

    const [root, stashtId] = urlObj.pathname.split("/").filter(Boolean);
    if (root !== "stash" || !stashtId) {
      return null;
    }

    return UUID_REGEX.test(stashtId) ? stashtId.toLowerCase() : null;
  } catch {
    return null;
  }
};

const isValidUrl = (url: string): boolean => {
  try {
    const normalized = normalizeUrl(url);
    const urlObj = new URL(normalized);
    return urlObj.protocol === "https:" || urlObj.protocol === "http:";
  } catch {
    return false;
  }
};

const resolveInputUrl = (input: string): string | null => {
  const trimmed = input.trim();
  if (isUuid(trimmed)) {
    return `https://stasht.app/stash/${trimmed.toLowerCase()}`;
  }

  if (extractStashtIdFromUrl(trimmed)) {
    return normalizeUrl(trimmed);
  }

  return isValidUrl(trimmed) ? normalizeUrl(trimmed) : null;
};

// -- Platform detection -----------------------------------------------------

const detectPlatform = (url: string): string => {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    const match = (domain: string) => hostname === domain || hostname.endsWith(`.${domain}`);
    if (match("instagram.com") || match("instagr.am")) return "instagram";
    if (match("tiktok.com")) return "tiktok";
    if (match("twitter.com") || match("x.com") || match("t.co")) return "twitter";
    if (match("facebook.com")) return "facebook";
    if (match("youtube.com") || match("youtu.be")) return "youtube";
    if (match("reddit.com") || match("redd.it")) return "reddit";
  } catch {
    // invalid URL
  }
  return "webpage";
};

// -- Presigned upload helpers -----------------------------------------------

interface PresignResponse {
  upload_url: string;
  file_url: string;
  s3_key: string;
}

const getPresignedUrl = (filename: string, fileType: string, fileSize: number): Promise<PresignResponse> =>
  apiClient.post<PresignResponse>("/upload/presign", {
    filename,
    file_type: fileType,
    file_size: fileSize,
  });

const uploadFileToS3 = async (presignedUrl: string, file: File): Promise<void> => {
  const response = await fetch(presignedUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`);
  }
};

// -- File validation --------------------------------------------------------

const ACCEPTED_MIME_SET = new Set([
  ...ACCEPTED_IMAGE_TYPES.split(","),
  "video/mp4",
  "video/quicktime",
  "video/webm",
]);

const validateFiles = (files: File[]): string | null => {
  for (const file of files) {
    if (!ACCEPTED_MIME_SET.has(file.type)) {
      return `Unsupported file type: ${file.type || file.name}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `${file.name} is too large. Maximum size is 500 MB.`;
    }
  }
  return null;
};

// -- Media type helper ------------------------------------------------------

const mediaTypeForFile = (file: File): "image" | "video" =>
  file.type.startsWith("video/") ? "video" : "image";

// ===========================================================================
// Component
// ===========================================================================

export function AddContentModal({
  isOpen,
  onClose,
  onSaveLink,
  contentType = "link",
  initialUrl,
  initialNote,
  initialTagName,
  initialFiles,
  disableInputs = false,
  disableAddAnother = false,
}: AddContentModalProps) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [modalState, setModalState] = useState<ModalState>("input");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { refreshAuth } = useAuth();

  // Image state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  // Tags state
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [orderedQuickTags, setOrderedQuickTags] = useState<Tag[]>([]);

  // Collections state
  const [selectedCollections, setSelectedCollections] = useState<Collection[]>([]);
  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const { collections: allCollections } = useWritableCollections();

  // Reminder state (refs for synchronous access during submit)
  const [reminderFireAtMs, _setReminderFireAtMs] = useState<number | null>(null);
  const [reminderNote, _setReminderNote] = useState<string | undefined>(undefined);
  const [reminderPickerOpen, setReminderPickerOpen] = useState(false);
  const reminderFireAtMsRef = useRef<number | null>(null);
  const reminderNoteRef = useRef<string | undefined>(undefined);

  const setReminderFireAtMs = useCallback((val: number | null) => {
    reminderFireAtMsRef.current = val;
    _setReminderFireAtMs(val);
  }, []);
  const setReminderNote = useCallback((val: string | undefined) => {
    reminderNoteRef.current = val;
    _setReminderNote(val);
  }, []);

  const isImageMode = contentType === "image";

  // -- Tag loading ----------------------------------------------------------

  useEffect(() => {
    if (isOpen) {
      loadTags();
    }
  }, [isOpen]);

  const loadTags = async () => {
    setTagsLoading(true);
    try {
      const tags = await tagsService.listTags();
      setAllTags(tags);
      setOrderedQuickTags(sortTags(tags, "recent"));
    } catch (loadError) {
      console.error("Failed to load tags:", loadError);
    } finally {
      setTagsLoading(false);
    }
  };

  // -- Reset state on open --------------------------------------------------

  const clearImageState = useCallback(() => {
    // Revoke object URLs to avoid memory leaks
    previewUrls.forEach((u) => URL.revokeObjectURL(u));
    setSelectedFiles([]);
    setPreviewUrls([]);
    setUploadProgress(null);
  }, [previewUrls]);

  useEffect(() => {
    if (isOpen) {
      console.log("[AddContentModal] Modal opened, resetting state");
      const resolvedInitial = initialUrl ? resolveInputUrl(initialUrl) : null;
      setUrl(resolvedInitial ?? initialUrl ?? "");
      setTitle("");
      setNote(initialNote ?? "");
      setModalState("input");
      setError(null);
      setSelectedTags([]);
      setOrderedQuickTags([]);
      setSelectedCollections([]);
      setUploadProgress(null);
      setReminderFireAtMs(null);
      setReminderNote(undefined);
      setReminderPickerOpen(false);

      // Revoke previous object URLs before creating new ones
      setPreviewUrls((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });

      // Pre-populate with files from FAB, or clear
      if (initialFiles && initialFiles.length > 0) {
        setSelectedFiles(initialFiles);
        setPreviewUrls(initialFiles.map((f) => URL.createObjectURL(f)));
      } else {
        setSelectedFiles([]);

        // On native, auto-open file picker; on web, show the drop zone instead
        if (isImageMode && isNativeApp()) {
          const id = window.setTimeout(() => fileInputRef.current?.click(), 200);
          return () => window.clearTimeout(id);
        }
      }

      if (!isImageMode) {
        const id = window.setTimeout(() => inputRef.current?.focus(), 120);
        return () => window.clearTimeout(id);
      }
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialUrl, initialNote, isImageMode]);

  // -- Auto-select initial tag ----------------------------------------------

  useEffect(() => {
    if (!isOpen) return;
    if (!initialTagName) return;
    if (tagsLoading) return;

    const normalizedTarget = initialTagName.trim().toLowerCase();
    if (!normalizedTarget) return;

    const alreadySelected = selectedTags.some(tag => tag.name.trim().toLowerCase() === normalizedTarget);
    if (alreadySelected) return;

    const existing = allTags.find(tag => tag.name.trim().toLowerCase() === normalizedTarget);
    if (existing) {
      setSelectedTags(prev => [...prev, existing]);
      return;
    }

    void (async () => {
      try {
        const tag = await tagsService.getOrCreateTag(initialTagName.trim(), TAG_COLORS[1]);
        setAllTags(prev => prev.some(t => t.id === tag.id) ? prev : [...prev, tag]);
        setSelectedTags(prev => prev.some(t => t.id === tag.id) ? prev : [...prev, tag]);
      } catch (createError) {
        console.warn("[AddContentModal] Failed to create initial tag:", createError);
      }
    })();
  }, [allTags, initialTagName, isOpen, selectedTags, tagsLoading]);

  // -- File handling --------------------------------------------------------

  const addFiles = useCallback((incoming: File[]) => {
    const validationError = validateFiles(incoming);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFiles((prev) => [...prev, ...incoming]);
    setPreviewUrls((prev) => [...prev, ...incoming.map((f) => URL.createObjectURL(f))]);
  }, []);

  const removeFile = useCallback(
    (index: number) => {
      URL.revokeObjectURL(previewUrls[index]);
      setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
      setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    },
    [previewUrls],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files ?? []);
      if (files.length > 0) {
        addFiles(files);
      }
      // Reset the input so the same file(s) can be re-selected
      e.target.value = "";
    },
    [addFiles],
  );

  // -- Reminder helper ------------------------------------------------------

  const maybeCreateReminder = async (stashtId: string) => {
    if (!reminderFireAtMsRef.current) return;
    try {
      await notificationsService.createReminder({
        trigger: { kind: "absolute", fire_at_ms: reminderFireAtMsRef.current },
        target: { type: "stash", id: stashtId },
        payload: {
          title: "Stash Reminder",
          ...(reminderNoteRef.current ? { body: reminderNoteRef.current } : {}),
          deep_link: `/stash/${stashtId}`,
          data: { stash_id: stashtId, kind: "stash_reminder" },
        },
      });
    } catch (reminderErr) {
      console.error("Failed to create reminder:", reminderErr);
      toast.warning("Stashed, but reminder could not be set.");
    }
  };

  // -- Submit handlers ------------------------------------------------------

  const handleSubmitLink = async () => {
    const trimmed = url.trim();
    if (!trimmed) {
      setError("Enter a link to stash.");
      return;
    }

    const normalizedUrl = resolveInputUrl(trimmed);
    if (!normalizedUrl) {
      setError("Please enter a valid link.");
      return;
    }

    try {
      setError(null);
      setModalState("processing");

      const requestBody: Record<string, unknown> = {
        url: normalizedUrl,
        personal_note: note.trim() || "",
        media_urls: [],
        platform: detectPlatform(normalizedUrl),
        shared_at: new Date().toISOString(),
        source: "web_manual",
        share_sheet_variant: "web_manual",
        send_ready_notification: true,
      };

      const trimmedTitle = title.trim();
      if (trimmedTitle) {
        requestBody.user_title = trimmedTitle;
      }

      if (selectedTags.length > 0) {
        requestBody.tag_ids = selectedTags.map(tag => tag.id);
      }

      if (selectedCollections.length > 0) {
        requestBody.collection_ids = selectedCollections.map(c => c.collection_id);
      }

      const response = await apiClient.post<SaveResponse>("/saves", requestBody);

      // stash_created fires server-side via log_activity(STASH_SAVED).

      await maybeCreateReminder(response.stasht_id);

      setModalState("success");
    } catch (err) {
      console.error("Failed to save share", err);
      const message = err instanceof Error ? err.message : "Unable to stash link right now.";
      setError(message);
      setModalState("input");
      toast.error("Could not stash that link.");
    }
  };

  const handleSubmitImages = async () => {
    if (selectedFiles.length === 0) {
      setError("Select at least one image to stash.");
      return;
    }

    try {
      setError(null);
      setModalState("processing");

      // Upload each file: presign then PUT to S3
      const mediaEntries: { url: string; s3_key: string; type: "image" | "video" }[] = [];
      const fileUrls: string[] = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        setUploadProgress(`Uploading ${i + 1} of ${selectedFiles.length}...`);

        const presign = await getPresignedUrl(file.name, file.type, file.size);
        await uploadFileToS3(presign.upload_url, file);

        mediaEntries.push({
          url: presign.file_url,
          s3_key: presign.s3_key,
          type: mediaTypeForFile(file),
        });
        fileUrls.push(presign.file_url);
      }

      setUploadProgress("Saving...");

      const requestBody: Record<string, unknown> = {
        media: mediaEntries,
        media_urls: fileUrls,
        caption: note.trim() || "",
        platform: "upload",
        source: "web_manual",
        shared_at: new Date().toISOString(),
        share_sheet_variant: "web_manual",
        send_ready_notification: true,
      };

      const trimmedTitle = title.trim();
      if (trimmedTitle) {
        requestBody.user_title = trimmedTitle;
      }

      if (selectedTags.length > 0) {
        requestBody.tag_ids = selectedTags.map(tag => tag.id);
      }

      if (selectedCollections.length > 0) {
        requestBody.collection_ids = selectedCollections.map(c => c.collection_id);
      }

      const response = await apiClient.post<SaveResponse>("/saves", requestBody);

      // stash_created fires server-side via log_activity(STASH_SAVED).

      await maybeCreateReminder(response.stasht_id);

      setUploadProgress(null);
      setModalState("success");
    } catch (err) {
      console.error("Failed to upload images", err);
      const message = err instanceof Error ? err.message : "Unable to upload images right now.";
      setError(message);
      setModalState("input");
      setUploadProgress(null);
      toast.error(message);
    }
  };

  const doSubmit = async () => {
    if (isImageMode) {
      await handleSubmitImages();
    } else {
      await handleSubmitLink();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await doSubmit();
  };

  // -- Close / reset --------------------------------------------------------

  const handleClose = useCallback(() => {
    if (modalState === "success" && url && onSaveLink) {
      onSaveLink(url);
    }

    if (modalState === "success") {
      queryClient.invalidateQueries({ queryKey: ["stashes"] });
      queryClient.invalidateQueries({ queryKey: ["collections"] });
    }

    onClose();

    setTimeout(() => {
      setUrl("");
      setTitle("");
      setNote("");
      setSelectedTags([]);
      setSelectedCollections([]);
      setModalState("input");
      setError(null);
      clearImageState();
      setReminderFireAtMs(null);
      setReminderNote(undefined);
    }, 300);
  }, [modalState, url, onSaveLink, onClose, queryClient, clearImageState, setReminderFireAtMs, setReminderNote]);

  // -- Auto-close success screen --------------------------------------------

  useEffect(() => {
    if (modalState !== "success") return;
    const id = window.setTimeout(() => handleClose(), 3000);
    return () => window.clearTimeout(id);
  }, [modalState, handleClose]);

  const handleAddAnother = () => {
    queryClient.invalidateQueries({ queryKey: ["stashes"] });
    refreshAuth();

    setUrl("");
    setTitle("");
    setNote("");
    setSelectedTags([]);
    setSelectedCollections([]);
    setModalState("input");
    setError(null);
    clearImageState();
    setReminderFireAtMs(null);
    setReminderNote(undefined);
  };

  // -- Tag helpers ----------------------------------------------------------

  const handleTagsChange = async (tagIds: string[]) => {
    const freshTags = await tagsService.listTags();
    setAllTags(freshTags);

    const tags = freshTags.filter(tag => tagIds.includes(tag.id));
    setSelectedTags(tags);

    const selectedIds = new Set(tagIds);
    const sortedByRecent = sortTags(freshTags, "recent");
    const selectedFirst = sortedByRecent.filter(tag => selectedIds.has(tag.id));
    const unselected = sortedByRecent.filter(tag => !selectedIds.has(tag.id));
    setOrderedQuickTags([...selectedFirst, ...unselected]);
  };

  const toggleQuickTag = (tag: Tag) => {
    if (selectedTags.some(t => t.id === tag.id)) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  // -- Collection helpers ---------------------------------------------------

  const handleCollectionsChange = (collectionIds: string[]) => {
    const selected = allCollections.filter(c => collectionIds.includes(c.collection_id));
    setSelectedCollections(selected);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  // -- Dynamic copy ---------------------------------------------------------

  const getTitle = (): string => {
    const titlesByState: Record<ModalState, string> = {
      input: disableInputs ? "Your first stasht" : isImageMode ? "Add Images" : "Add to Stasht",
      processing: isImageMode ? "Uploading" : "Add to Stasht",
      success: "Stashed it!",
    };
    return titlesByState[modalState];
  };

  const getDescription = (): string => {
    const descriptionsByState: Record<ModalState, string> = {
      input: disableInputs
        ? "We've filled this one in for you. Just tap Stash It!"
        : isImageMode
          ? selectedFiles.length > MAX_IMAGE_COUNT
            ? `${selectedFiles.length}/${MAX_IMAGE_COUNT} — Maximum is ${MAX_IMAGE_COUNT}.`
            : selectedFiles.length > 0
              ? `${selectedFiles.length}/${MAX_IMAGE_COUNT} images selected.`
              : "Select up to 10 images per stash."
          : (error || "Drop any link and we'll do the rest."),
      processing: isImageMode ? (uploadProgress ?? "Uploading your files...") : "We're working our magic",
      success: isImageMode
        ? "Your images have been successfully Stashed."
        : "Your link has been successfully Stashed.",
    };
    return descriptionsByState[modalState];
  };

  // -- Submit button state --------------------------------------------------

  const isSubmitDisabled = isImageMode
    ? selectedFiles.length === 0 || selectedFiles.length > MAX_IMAGE_COUNT
    : !url.trim();

  // -- Render ---------------------------------------------------------------

  return (
    <UnifiedModal
      open={isOpen}
      onOpenChange={handleOpenChange}
      icon={isImageMode ? <ImageIcon /> : <Link />}
      title={getTitle()}
      description={getDescription()}
      descriptionClassName={selectedFiles.length > MAX_IMAGE_COUNT ? "text-destructive" : undefined}
      contentClassName="max-w-[90vw] sm:max-w-md"
    >
      {/* Hidden file input for image mode */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES}
        multiple
        className="hidden"
        onChange={handleFileInputChange}
      />

      <AnimatePresence mode="wait">
        {/* Input State */}
        {modalState === "input" && (
          <motion.div
            key="input"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="grid gap-4 pt-2 pb-4">
              {/* Image previews (image mode only) */}
              {isImageMode && (
                <ImagePreviewRow
                  previewUrls={previewUrls}
                  files={selectedFiles}
                  onRemove={removeFile}
                  onAddMore={() => fileInputRef.current?.click()}
                  onDropFiles={addFiles}
                />
              )}

              {/* URL input (link mode only) */}
              {!isImageMode && (
                <ClipboardUrlInputRow
                  active={isOpen}
                  value={url}
                  onChange={setUrl}
                  onClearError={() => {
                    if (error) setError(null);
                  }}
                  onUserError={(message) => toast.error(message)}
                  disabled={disableInputs}
                  disableClipboardRead={Boolean(initialUrl)}
                  inputRef={inputRef}
                  inputId="share-url"
                  placeholder="Paste any link..."
                  onFocus={handleInputFocus}
                  hideAutoPastePrompt={disableInputs || Boolean(initialUrl)}
                  transformPastedText={(raw) => parseHttpUrlOrNull(raw)}
                />
              )}

              {/* Title Input */}
              <Input
                type="text"
                placeholder="Title (optional)"
                value={title}
                disabled={disableInputs}
                onChange={e => setTitle(e.target.value)}
                onFocus={handleInputFocus}
                maxLength={200}
              />

              {/* Note Textarea */}
              <Textarea
                id="share-note"
                placeholder={isImageMode
                  ? "Description (optional). Restaurant rec, gift idea, price list, etc."
                  : "Private note (optional). Why you're stashing this, only you will see it."}
                className="resize-none text-pretty"
                rows={3}
                value={note}
                disabled={disableInputs}
                onChange={e => setNote(e.target.value)}
                onFocus={handleInputFocus}
              />

              {/* Tags + Remind Me Section */}
              <div className="grid gap-2">
                {/* Quick-tag pills */}
                {!tagsLoading && allTags.length > 0 && (
                  <div className="w-full overflow-x-auto scrollbar-hide">
                    <div className="flex gap-2 pb-1">
                      {(() => {
                        const selectedIds = new Set(selectedTags.map(t => t.id));
                        const orderedTags = orderedQuickTags.length > 0 ? orderedQuickTags : sortTags(allTags, "recent");

                        return orderedTags.map(tag => {
                          const isSelected = selectedIds.has(tag.id);
                          return (
                            <TagPill
                              key={tag.id}
                              name={tag.name}
                              color={tag.color}
                              selected={isSelected}
                              suggested={isSuggestedTag(tag)}
                              onClick={disableInputs ? undefined : () => toggleQuickTag(tag)}
                              disabled={disableInputs}
                            />
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

                {/* Action row: Add Tags (left), Remind Me (right) */}
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setTagPickerOpen(true)}
                    className={cn(LABEL, "h-7 gap-1 px-0")}
                    disabled={disableInputs}
                  >
                    <Plus className="h-3 w-3" />
                    Add Tags
                  </Button>
                  <Button
                    type="button"
                    variant={reminderFireAtMs ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => setReminderPickerOpen(true)}
                    className={cn(LABEL, "h-7 gap-1 px-0", reminderFireAtMs && "text-brand-accent")}
                    disabled={disableInputs}
                  >
                    <AlarmClock className="h-3 w-3" />
                    {reminderFireAtMs
                      ? formatDistanceToNow(new Date(reminderFireAtMs), { addSuffix: true })
                      : "Remind Me"}
                  </Button>
                </div>
              </div>

              <Button type="submit" variant="gradient" size="modal" disabled={isSubmitDisabled}>
                Stash It
              </Button>

              {/* Collection bar — Pinterest-style bottom selector */}
              <button
                type="button"
                onClick={() => !disableInputs && setCollectionPickerOpen(true)}
                disabled={disableInputs}
                className={cn(
                  "flex items-center justify-between w-full rounded-full px-5 py-3",
                  "bg-muted/80",
                  "transition-colors",
                  !disableInputs && "active:bg-muted",
                  disableInputs && "opacity-50 cursor-not-allowed",
                )}
              >
                <div className="flex flex-col items-start min-w-0">
                  <span className="text-ui-micro text-muted-foreground leading-tight">
                    {selectedCollections.length > 0 ? "Saving to" : ""}
                  </span>
                  <span className={cn(ROW_TITLE, "text-foreground truncate max-w-[260px]")}>
                    {selectedCollections.length === 0
                      ? "Save to collection"
                      : selectedCollections.length === 1
                        ? selectedCollections[0].title
                        : `${selectedCollections[0].title} +${selectedCollections.length - 1}`}
                  </span>
                </div>
                <Pencil className="h-4 w-4 text-muted-foreground flex-shrink-0 ml-3" />
              </button>
            </form>

            <TagPickerStacked
              open={tagPickerOpen}
              onOpenChange={setTagPickerOpen}
              messageId=""
              selectedTagIds={selectedTags.map(t => t.id)}
              onTagsChange={handleTagsChange}
            />

            <CollectionPickerStacked
              open={collectionPickerOpen}
              onOpenChange={setCollectionPickerOpen}
              selectedCollectionIds={selectedCollections.map(c => c.collection_id)}
              onCollectionsChange={handleCollectionsChange}
            />

            <ReminderFormModal
              open={reminderPickerOpen}
              onOpenChange={(v) => { if (!v) setReminderPickerOpen(false); }}
              stacked
              actionLabel="Stash It"
              onBack={() => setReminderPickerOpen(false)}
              onSave={async (fireAtMs, reminderNoteVal) => {
                setReminderFireAtMs(fireAtMs);
                setReminderNote(reminderNoteVal);
              }}
              onSaved={() => {
                setReminderPickerOpen(false);
                doSubmit();
              }}
            />
          </motion.div>
        )}

        {/* Processing State */}
        {modalState === "processing" && (
          <motion.div
            key="processing"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center justify-center pt-4 pb-12"
          >
            <div className="w-20 h-20 rounded-full bg-brand-gradient flex items-center justify-center mb-6 overflow-hidden">
              <img src="/Stash'n.gif" alt="Processing" className="h-16 w-16 object-contain" />
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={cn(CARD_TITLE, "text-foreground mb-2 text-center")}
            >
              {isImageMode ? (uploadProgress ?? "Uploading...") : "Finding the good stuff...."}
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className={cn(META, "text-center")}
            >
              {isImageMode ? "Stashing your images" : "Grabbing details \u2022 Adding context"}
            </motion.p>
          </motion.div>
        )}

        {/* Success State */}
        {modalState === "success" && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col items-center justify-center pt-4 pb-0"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: "spring", bounce: 0.5 }}
              className="w-20 h-20 rounded-full bg-brand-gradient flex items-center justify-center mb-6"
            >
              <Check className="h-10 w-10 text-white" strokeWidth={3} />
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className={cn(SECTION_TITLE, "text-foreground mb-2")}
            >
              You&apos;re all set!
            </motion.h3>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className={cn(META, "text-center mb-6")}
            >
              We&apos;ll continue to add more details in the background.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex gap-3 w-full"
            >
              {!disableAddAnother && (
                <Button variant="secondary" size="modal" onClick={handleAddAnother} className="flex-1">
                  Add Another
                </Button>
              )}
              <Button variant="gradient" size="modal" onClick={handleClose} className="flex-1">
                Done
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </UnifiedModal>
  );
}

// ===========================================================================
// Image Preview Row
// ===========================================================================

interface ImagePreviewRowProps {
  previewUrls: string[];
  files: File[];
  onRemove: (index: number) => void;
  onAddMore: () => void;
  onDropFiles: (files: File[]) => void;
}

function ImagePreviewRow({ previewUrls, files, onRemove, onAddMore, onDropFiles }: ImagePreviewRowProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const MAX_VISIBLE = 3;
  const hasOverflow = !expanded && previewUrls.length > MAX_VISIBLE;
  const visibleUrls = hasOverflow ? previewUrls.slice(0, MAX_VISIBLE) : previewUrls;
  const overflowCount = previewUrls.length - MAX_VISIBLE;

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) {
      onDropFiles(dropped);
    }
  }, [onDropFiles]);

  if (previewUrls.length === 0) {
    if (isNativeApp()) {
      return (
        <button
          type="button"
          onClick={onAddMore}
          className="flex items-center justify-center gap-2 h-12 rounded-lg bg-muted text-muted-foreground active:scale-95 transition-transform"
        >
          <Plus className="h-4 w-4" />
          <span className={ROW_TITLE}>Add images</span>
        </button>
      );
    }

    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          "flex items-center justify-center h-20 rounded-lg border-2 border-dashed transition-colors",
          isDragOver
            ? "border-brand-accent bg-brand-accent/10 text-foreground"
            : "border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/50",
        )}
      >
        <p className={ROW_TEXT}>
          Drag images here or{" "}
          <button
            type="button"
            onClick={onAddMore}
            className="font-medium text-brand-accent hover:text-brand-accent/80 underline underline-offset-2 transition-colors"
          >
            select files
          </button>
        </p>
      </div>
    );
  }

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        "w-full overflow-x-auto scrollbar-hide pt-2 pr-2 rounded-lg border-2 border-dashed transition-colors",
        isDragOver
          ? "border-brand-accent bg-brand-accent/10"
          : "border-transparent",
      )}
    >
      <div className="flex gap-2 pb-1">
        {visibleUrls.map((previewUrl, index) => {
          const file = files[index];
          const isVideo = file?.type.startsWith("video/");
          const isLastVisible = index === MAX_VISIBLE - 1 && hasOverflow;

          return (
            <div key={previewUrl} className="relative flex-shrink-0">
              {isVideo ? (
                <video
                  src={previewUrl}
                  className="h-20 w-20 rounded-lg object-cover"
                  muted
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={file?.name ?? "Preview"}
                  className="h-20 w-20 rounded-lg object-cover"
                />
              )}

              {isLastVisible && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="absolute inset-0 rounded-lg bg-black/50 flex items-center justify-center"
                >
                  <span className={cn(ROW_TITLE, "text-white")}>+{overflowCount}</span>
                </button>
              )}

              <button
                type="button"
                onClick={() => onRemove(index)}
                className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full bg-foreground text-background flex items-center justify-center shadow-sm"
                aria-label={`Remove ${file?.name ?? "file"}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}

        {/* Add more button */}
        <button
          type="button"
          onClick={onAddMore}
          className="flex-shrink-0 h-20 w-20 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center text-muted-foreground hover:border-muted-foreground/50 transition-colors"
          aria-label="Add more images"
        >
          <Plus className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
